import { useEffect, useMemo, useState } from 'react';
import type { LeadCloudFileType } from '@econtractor/types';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import * as apiDocument from 'api/apiDocument';
import { getCloudflareUploadUrls } from 'api/apiCloudflare';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { buildDocumentMailContent } from 'utils/helpers/email/buildMailContent';
import { createPdfBlob } from 'utils/helpers/pdf/pdfMake';
import { buildProposalPdfDefinition } from '../../share/model/helper/proposalPdfDefinition';
import { fileToPdfCompatibleDataUrl } from '../../share/model/helper/fileToPdfCompatibleDataUrl';
import useTemplateValueKeys from '../../share/model/useTemplateValueKeys';
import {
    getProposalTotalAmount,
    getProposalUseUsd,
} from '../../share/model/helper/proposalPriceUtils';
import type { ProposalEditFormValues } from '../../Proposal/types';

interface UseDocumentGenerateParams {
    objectId?: number | string;
    objectName?: string;
    clientEmail?: string;
    dirProposalId?: number | null;
    formData: ProposalEditFormValues;
    rate: number;
    objectData?: unknown;
    onGenerated?: () => Promise<void> | void;
}

const useDocumentGenerate = ({
    objectId,
    objectName = '',
    clientEmail: initialClientEmail = '',
    dirProposalId = null,
    formData,
    rate,
    objectData,
    onGenerated,
}: UseDocumentGenerateParams) => {
    const { t } = useTranslation(['Document', 'common']);
    const templateValueKeys = useTemplateValueKeys();
    const { showToast } = useToast();
    const sourceEmail = String(initialClientEmail || '').trim();
    const [sendToClient, setSendToClient] = useState(false);
    const [clientEmail, setClientEmail] = useState(sourceEmail);
    const [generating, setGenerating] = useState(false);

    const canGenerate = useMemo(() => {
        const targetObjectId = Number(objectId || 0);
        if (!targetObjectId) return false;
        if (sendToClient && !String(clientEmail || '').trim()) return false;
        return true;
    }, [objectId, sendToClient, clientEmail]);

    useEffect(() => {
        if (!sourceEmail) return;
        setClientEmail((prev) => (String(prev || '').trim() ? prev : sourceEmail));
    }, [sourceEmail]);

    const handleSendToClientChange = (checked: boolean) => {
        setSendToClient(checked);
        if (checked && sourceEmail) {
            setClientEmail(sourceEmail);
        }
    };

    const getPdfBlobFromDefinition = async (docDefinition: unknown): Promise<Blob> => {
        try {
            return await createPdfBlob(docDefinition);
        } catch {
            throw new Error('document_pdf_blob_empty');
        }
    };

    const buildPdfBlob = async (): Promise<Blob> => {
        const hasLogo = Boolean(String(formData.proposalLogo || '').trim());
        const hasBackground = Boolean(String(formData.proposalBackground || '').trim());
        const docDefinition = await buildProposalPdfDefinition({
            t: (key: string, options?: Record<string, unknown>) => t(key, options || {}),
            templateValueKeys,
            formData,
            rate,
            generatedAt: Date.now(),
            logoDataUrl: hasLogo ? await fileToPdfCompatibleDataUrl(String(formData.proposalLogo || '')) : '',
            backgroundDataUrl: hasBackground
                ? await fileToPdfCompatibleDataUrl(String(formData.proposalBackground || ''))
                : '',
            resolveImageDataUrl: fileToPdfCompatibleDataUrl,
            objectData,
            includeCoverPage: false,
        });
        return await getPdfBlobFromDefinition(docDefinition);
    };

    const uploadPdfToCloudflare = async (
        objectIdValue: number,
        file: File,
    ): Promise<LeadCloudFileType | null> => {
        const uploadConfigs = await getCloudflareUploadUrls(
            [
                {
                    fileName: file.name,
                    contentType: file.type || 'application/pdf',
                    fileSize: file.size,
                },
            ],
            { objectId: objectIdValue },
        );
        if (!uploadConfigs?.length) return null;

        const uploaded = await uploadFilesWithOverallProgress([file], uploadConfigs);
        const uploadedFile = uploaded?.[0];
        if (!uploadedFile?.success) return null;
        return uploadedFile.config;
    };

    const handleGenerate = async (): Promise<boolean> => {
        if (generating) return false;
        const targetObjectId = Number(objectId || 0);
        if (!targetObjectId) {
            showToast(t('common:messError'), 'danger');
            return false;
        }
        if (sendToClient && !String(clientEmail || '').trim()) {
            showToast(t('edit.generate.clientEmailRequired'), 'warning');
            return false;
        }

        setGenerating(true);
        try {
            const pdfBlob = await buildPdfBlob();
            const baseName = String(formData.proposalName || '')
                .trim()
                .replace(/[\\/:*?"<>|]+/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
            const fileName = `${baseName || t('edit.generate.fileNameFallback')}.pdf`;
            const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
            const cloudFile = await uploadPdfToCloudflare(targetObjectId, pdfFile);
            if (!cloudFile) {
                showToast(t('common:messError'), 'danger');
                return false;
            }

            const email = String(clientEmail || '').trim();
            const comment = String(formData.proposalComment || '').trim().slice(0, 500);

            const logCreated = await apiDocument.createObjectDocumentGeneration(targetObjectId, {
                dirProposalId: Number(dirProposalId || 0),
                proposalName: formData.proposalName,
                proposalNumber: String(formData.proposalNumber || '').trim() || null,
                proposalDt: formData.proposalDt && formData.proposalDt > 0 ? formData.proposalDt : null,
                proposalType: formData.proposalType,
                file: cloudFile,
                sentToEmail: sendToClient && Boolean(email),
                sentEmail: sendToClient ? email : '',
                generationComment: comment,
                proposalAmount: getProposalTotalAmount(formData, rate),
                proposalUsd: getProposalUseUsd(formData.pricesInUsd, rate),
                proposalVat: Boolean(formData.proposalVat),
                products: formData.products || [],
                additionalItems: formData.additionalItems || [],
                pricesInUsd: Boolean(formData.pricesInUsd),
            });
            if (!logCreated?.stat) {
                showToast(t('common:messError'), 'danger');
                return false;
            }

            if (sendToClient && email) {
                const mailContent = await buildDocumentMailContent({
                    type: formData.proposalType || 'other',
                    documentName: formData.proposalName || cloudFile.fileName || 'document',
                    fileUrl: cloudFile.publicUrl,
                });
                if (mailContent) {
                    await apiDocument.sendObjectDocumentEmail(targetObjectId, {
                        email,
                        file: cloudFile,
                        proposalName: formData.proposalName,
                        objectName,
                        proposalType: formData.proposalType,
                        mailContent,
                    });
                }
            }

            showToast(t('edit.generate.successCreated'), 'success');
            if (onGenerated) await onGenerated();
            return true;
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
            return false;
        } finally {
            setGenerating(false);
        }
    };

    return {
        sendToClient,
        setSendToClient: handleSendToClientChange,
        clientEmail,
        setClientEmail,
        generating,
        canGenerate,
        handleGenerate,
    };
};

export default useDocumentGenerate;
